// D3GeoMap.jsx
import React, { useEffect, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { feature } from 'topojson-client';
import FloatingTooltip from './FloatingTooltip';

import washingtonTopoJSON from '../data/simplified_map.json';
import waSimplified from '../data/WashingtonStateSimple.json';

const D3GeoMap = ({ demandData }) => {
  const svgRef = useRef(null);

  // --- Tooltip State ---
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  // Use a ref for coordinates to avoid re-rendering on each mouse move
  const tooltipCoordsRef = useRef({ x: null, y: null });
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipColor, setTooltipColor] = useState('#e4e4e7'); // Default color


  useEffect(() => {
    const width = 200;
    const height = 160;

    const svg = select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const projection = geoMercator();
    const pathGenerator = geoPath().projection(projection);

    const countyGeoJSON = feature(
      washingtonTopoJSON,
      washingtonTopoJSON.objects['working']
    );
  
    const waGeoJSON = feature(
      waSimplified,
      waSimplified.objects['WashingtonState']
    );

    // Fit to a slightly smaller box to create a margin
    projection.fitExtent(
      [[5, 5], [width - 5, height - 5]],
      waGeoJSON
    );

    const getColor = (demand) => {
      switch (demand) {
        case 1:
          return '#10b981'; // Green
        case 2:
          return '#f43f5e'; // Red
        case 3:
          return '#64748b'; // Gray
        default:
          return '#e4e4e7'; // Light gray
      }
    };

    // Draw state boundary (halo and white border)
    const stateDemand = demandData.find((item) => item.id === 'Washington State');
    const stateBorderColor = getColor(stateDemand?.value);

    // Halo effect (outer layer)
    svg.selectAll('.state-boundary-halo')
      .data(waGeoJSON.features)
      .join('path')
      .attr('class', 'state-boundary-halo')
      .attr('d', pathGenerator)
      .attr('fill', 'none')
      .attr('stroke', 'white')
      .attr('stroke-width', 12);

    // White border (middle layer)
    svg.selectAll('.state-boundary-white')
      .data(waGeoJSON.features)
      .join('path')
      .attr('class', 'state-boundary-white')
      .attr('d', pathGenerator)
      .attr('fill', 'none')
      .attr('stroke', stateBorderColor)
      .attr('stroke-width', 7)
      .attr('stroke-opacity', 0.5);

    // Draw counties after the state boundary
    svg.selectAll('.county')
      .data(countyGeoJSON.features)
      .join('path')
      .attr('class', 'county')
      .attr('d', pathGenerator)
      .attr('fill', (d) => {
        const countyName = d.properties.JURISDIC_2;
        const demand = demandData.find((item) => item.id === countyName)?.value || null;
        return getColor(demand);
      })
      .attr('stroke', '#000')
      .attr('stroke-width', 0.5)
      .on('mouseover', function (event, d) {
        select(this).raise();
        select(this).attr('stroke', 'yellow').attr('stroke-width', 2);

        const countyName = d.properties.JURISDIC_2;
        const demand = demandData.find((item) => item.id === countyName)?.value || 'No Data';

        // Map demand levels to descriptive text
        const demandText = {
          1: "In Demand",
          2: "Not In Demand",
          3: "Balanced",
        };

        const demandDescription = demand ? demandText[demand] || "No Data" : "No Data";

        setTooltipText(`${countyName}: ${demandDescription}`);
        setTooltipColor(getColor(demand));
        setTooltipVisible(true);
      })
      .on('mousemove', function (event) {
        // Update tooltip coordinates directly (no debounce to avoid stale state)
        tooltipCoordsRef.current = { x: event.clientX, y: event.clientY };
      })
      .on('mouseout', function () {
        select(this).attr('stroke', '#000').attr('stroke-width', 0.5);
        setTooltipVisible(false);
        setTooltipText('');
      });
  
    return () => {
      // Clean up: remove all elements and reset tooltip state
      svg.selectAll('*').remove();
      setTooltipVisible(false);
      setTooltipText('');
      tooltipCoordsRef.current = { x: null, y: null };
    };
  }, [demandData]);

  // --- Render the SVG and the FloatingTooltip ---
  return (
    <div className="relative">
      <svg ref={svgRef} />
      <FloatingTooltip
        open={isTooltipVisible}
        coordsRef={tooltipCoordsRef}
        text={tooltipText}
        color={tooltipColor}
      />
    </div>
  );
};

export default D3GeoMap;